import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Bot, Briefcase, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import api from '../services/api';

import CopilotHeader from './copilot/CopilotHeader';
import CopilotInput from './copilot/CopilotInput';
import CopilotMessages from './copilot/CopilotMessages';
import CopilotWelcome from './copilot/CopilotWelcome';

/**
 * <BaselinxCopilot /> — floating AI assistant docked to the bottom-right.
 *
 * Previously a 479-line god-component. Responsibilities are now:
 *   - Session + auth bookkeeping          (this file)
 *   - Send/clear/retry message flow       (this file)
 *   - Header / welcome / messages / input (separate files under ./copilot)
 *   - Suggestion catalog + context logic  (./copilot/suggestions.js)
 */
const BaselinxCopilot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // Build the routing-context object passed to the API and to the welcome view.
  const getCurrentContext = () => {
    const path = location.pathname;
    let activeTab = 'none';
    if (path.includes('/app')) {
      activeTab = localStorage.getItem('baselinx_active_tab') || 'overview';
    }
    return { page: path, activeTab };
  };

  // Fresh session id on each login; `Date.now()` salts so reloads produce a new one.
  useEffect(() => {
    if (isAuthenticated && user) {
      setSessionId(`user_${user.id}_${Date.now()}`);
    }
  }, [isAuthenticated, user]);

  // Auto-scroll to the newest message whenever the list grows.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Put focus on the composer every time the panel is opened.
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const handleSendMessage = async (customMessage = null) => {
    const textToSend = customMessage || message;
    if (!textToSend.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);
    setSuggestions([]);

    try {
      const response = await api.copilotChat(textToSend, sessionId, getCurrentContext());
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
        },
      ]);
      if (response.suggestions?.length) setSuggestions(response.suggestions);
    } catch (error) {
      console.error('Copilot error:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: "I'm sorry, I encountered an error. Please try again.",
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      await api.clearCopilotHistory();
      setMessages([]);
      setSuggestions([]);
      setSessionId(`user_${user?.id}_${Date.now()}`);
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      {!isOpen && menu && (
        <div className="fixed bottom-36 right-6 z-50 w-64 rounded-xl border border-slate-700 bg-slate-900/95 backdrop-blur-md shadow-2xl p-2 space-y-1" data-testid="copilot-chooser">
          <button onClick={() => { setMenu(false); setIsOpen(true); }} data-testid="copilot-choose-chat"
            className="w-full flex items-start gap-3 text-left rounded-lg px-3 py-2.5 hover:bg-cyan-500/10 transition-colors">
            <Sparkles className="w-4 h-4 text-cyan-300 mt-0.5" />
            <span><span className="block text-sm font-semibold text-white">Baselinx Copilot</span><span className="block text-[11px] text-slate-400">Ask anything about the platform</span></span>
          </button>
          <button onClick={() => { setMenu(false); navigate(isAuthenticated ? '/ai-agents' : '/login'); }} data-testid="copilot-choose-agents"
            className="w-full flex items-start gap-3 text-left rounded-lg px-3 py-2.5 hover:bg-cyan-500/10 transition-colors">
            <Bot className="w-4 h-4 text-cyan-300 mt-0.5" />
            <span><span className="block text-sm font-semibold text-white">AI Agents</span><span className="block text-[11px] text-slate-400">PLM agent command center{isAuthenticated ? '' : ' (sign in)'}</span></span>
          </button>
          {user?.role === 'super_admin' && (
            <button onClick={() => { setMenu(false); navigate('/admin/business-ai'); }} data-testid="copilot-choose-business"
              className="w-full flex items-start gap-3 text-left rounded-lg px-3 py-2.5 hover:bg-fuchsia-500/10 transition-colors">
              <Briefcase className="w-4 h-4 text-fuchsia-300 mt-0.5" />
              <span><span className="block text-sm font-semibold text-white">Business AI</span><span className="block text-[11px] text-slate-400">Leads, approvals, business agents</span></span>
            </button>
          )}
        </div>
      )}
      {!isOpen && (
        <button
          data-testid="copilot-open-btn"
          onClick={() => setMenu((m) => !m)}
          aria-expanded={menu}
          className="fixed bottom-20 right-6 w-14 h-14 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-full shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center group z-50 hover:scale-110"
        >
          {menu ? <X className="w-6 h-6 text-white" /> : <Sparkles className="w-6 h-6 text-white group-hover:animate-pulse" />}
          <span className={`pointer-events-none absolute -top-10 right-0 bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 ${menu ? '' : 'group-hover:opacity-100'} transition-opacity whitespace-nowrap border border-slate-700`}>
            Copilot & AI Agents
          </span>
        </button>
      )}

      {isOpen && (
        <div
          data-testid="copilot-panel"
          className={`fixed z-50 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl shadow-black/50 flex flex-col transition-all duration-300 ${
            isExpanded
              ? 'bottom-4 right-4 left-4 top-20 md:left-auto md:w-[600px] md:h-[700px]'
              : 'bottom-20 right-6 w-[380px] h-[550px]'
          }`}
        >
          <CopilotHeader
            isExpanded={isExpanded}
            onClear={handleClearHistory}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            onClose={() => setIsOpen(false)}
          />

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <CopilotWelcome context={getCurrentContext()} onSend={handleSendMessage} />
            ) : (
              <CopilotMessages
                messages={messages}
                isLoading={isLoading}
                suggestions={suggestions}
                onSend={handleSendMessage}
                scrollRef={messagesEndRef}
              />
            )}
          </div>

          <CopilotInput
            ref={inputRef}
            value={message}
            onChange={setMessage}
            onSend={() => handleSendMessage()}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default BaselinxCopilot;
